import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, useTheme, useMediaQuery } from '@mui/material';
import { AddBoxOutlined } from '@mui/icons-material';
import DevicesHeader from './DevicesHeader';
import DeviceList from './DeviceList';
import AddDeviceDialogBox from './AddDeviceDialogBox';
import ErrorSnackbar from '../../components/ErrorSnackbar';
import { useGetDevicesQuery } from '../../services/api';
import useSelectedGroup from '../../hooks/useSelectedGroup';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import useGroupOptions from '../../hooks/useGroupOptions';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import GroupSelector from '../../components/GroupSelector';

const Devices = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { role, company } = useSelector((state) => state.global);
    const isAdmin = role === 'Admin';
    const isSuperOrPowerAdmin = role === 'Super Admin' || role === 'Power Admin';

    const [openDialog, setOpenDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    // Company and group options
    const { companyOptions } = useCompanyOptions();
    const groupOptions = useGroupOptions(company);
    const { selectedCompanyId } = useSelectedCompany({ companyOptions });
    const { selectedGroup, handleGroupChange } = useSelectedGroup(groupOptions, isSuperOrPowerAdmin ? selectedCompanyId : company);

    // API
    const { data: devicesData, refetch: refetchDevices } = useGetDevicesQuery(company);

    // useEffect(() => {
    //     const interval = setInterval(() => refetchDevices(), 60000);
    //     return () => clearInterval(interval);
    // }, [refetchDevices]);

     // Ensure the latest data is fetched when component mounts
    useEffect(() => {
        refetchDevices(); 
    }, [refetchDevices]);

    // Filter data
    const filteredDevices = useMemo(() => {
        const selectedGroupObj = groupOptions.find((group) => group._id === selectedGroup);
        const selectedDeviceIds = selectedGroupObj?.deviceIds || [];

        return devicesData ? devicesData.filter((device) => device.addedToDashboard && device.company._id === company && selectedDeviceIds.includes(device._id)) : [];
    }, [devicesData, company, groupOptions, selectedGroup]);

    // Handle dialog
    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);
    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    const hasGroups = Array.isArray(groupOptions) && groupOptions.length > 0;

    return (
        <Box m={isSmallScreen ? '1rem' : '1rem 2.5rem'}>
            <DevicesHeader handleOpenDialog={handleOpenDialog} theme={theme} selectedGroup={selectedGroup} handleGroupChange={handleGroupChange} groupOptions={groupOptions} />

            {isSmallScreen && (
                <Box
                    display="flex"
                    flexDirection="column"
                    gap="1rem"
                    marginBottom="1rem"
                    padding="1rem"
                    backgroundColor={theme.palette.background.paper}
                    borderRadius="15px"
                    sx={{
                        boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    {hasGroups && <GroupSelector selectedGroup={selectedGroup} handleGroupChange={handleGroupChange} groupOptions={groupOptions} />}
                    {isAdmin && (
                        <Button
                            onClick={handleOpenDialog}
                            sx={{
                                backgroundColor: theme.palette.text.white,
                                color: theme.palette.text.black,
                                padding: '1rem',
                                width: '100%',
                                '&:hover': { backgroundColor: theme.palette.text.white },
                                '&:focus': { outline: 'none' },
                            }}
                        >
                            <AddBoxOutlined sx={{ marginRight: '8px' }} />
                            Add Device
                        </Button>
                    )}
                </Box>
            )}

            <DeviceList filteredDevices={filteredDevices} theme={theme} isSmallScreen={isSmallScreen} />
            <AddDeviceDialogBox openDialog={openDialog} handleCloseDialog={handleCloseDialog} setSnackbar={setSnackbar} theme={theme} refetchDevices={refetchDevices} />
            <ErrorSnackbar open={snackbar.open} onClose={handleSnackbarClose} message={snackbar.message} severity={snackbar.severity} />
        </Box>
    );
};

export default Devices;
